import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import CompraPendienteHiloInfoDialog from '../../components/compras/CompraPendienteHiloInfoDialog';
import comprasSeguimientoService, {
  CompraHiloComprobante,
  CompraPendienteMaster,
  esProductoInventariado,
  formatComprobanteHiloLine,
  formatComprobanteMasterDisplay,
  formatFechaEmisionComprobanteMaster,
} from '../../services/comprasSeguimientoService';
import proveedorService from '../../services/proveedorService';
import productoService from '../../services/productoService';
import { getApiErrorMessage } from '../../utils/apiError';
import BusquedaEnResultadosField from '../../components/shared/BusquedaEnResultadosField';
import { useFilasSeguimientoFiltradas } from '../../hooks/useFilasSeguimientoFiltradas';

const getDefaultDateRange = (): { from: string; to: string } => {
  const today = new Date();
  const from = new Date(today);
  from.setMonth(from.getMonth() - 3);

  const toIso = today.toISOString().slice(0, 10);
  const fromIso = from.toISOString().slice(0, 10);

  return { from: fromIso, to: toIso };
};

const toInputDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TABLE_MIN_WIDTH = 1120;
const RESULTADOS_PAGE_SIZE_DEFAULT = 50;
const ACTION_ICON_BUTTON_BASE_SX = {
  width: 32,
  height: 32,
  borderRadius: 1.25,
  color: '#fff',
  border: 'none',
};
const ACTION_ICON_BUTTON_SX = {
  verDetalle: {
    ...ACTION_ICON_BUTTON_BASE_SX,
    bgcolor: '#fb8c00',
    '&:hover': { bgcolor: '#ef6c00' },
  },
  nuevaFactura: {
    ...ACTION_ICON_BUTTON_BASE_SX,
    bgcolor: '#8e24aa',
    '&:hover': { bgcolor: '#6a1b9a' },
  },
  nuevoRemito: {
    ...ACTION_ICON_BUTTON_BASE_SX,
    bgcolor: '#43a047',
    '&:hover': { bgcolor: '#2e7d32' },
  },
};

interface ProveedorBusquedaItem {
  nroProveedor: number;
  nombre?: string;
}

interface ProductoBusquedaItem {
  codigoArticulo: string;
  descripcion?: string;
}

type FiltroFechaRapida = 'hoy' | 'ayer' | 'estaSemana' | 'ult3Meses' | 'ult6Meses' | 'todo' | 'custom';

type KpiTarjetaProps = {
  icon: React.ReactNode;
  titulo: string;
  valor: string;
  colorIcono: string;
};

const KpiTarjeta: React.FC<KpiTarjetaProps> = ({ icon, titulo, valor, colorIcono }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.75,
      height: '100%',
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          color: colorIcono,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${colorIcono}14`,
          borderRadius: 1.5,
          p: 0.85,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
          {titulo}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, mt: 0.25 }}>
          {valor}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const SeguimientoCompras: React.FC = () => {
  const defaultDateRange = getDefaultDateRange();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [nroProveedor, setNroProveedor] = useState<string>('');
  const [nombreProveedorFiltro, setNombreProveedorFiltro] = useState<string>('');
  const [dialogProveedorOpen, setDialogProveedorOpen] = useState<boolean>(false);
  const [proveedorSearchTerm, setProveedorSearchTerm] = useState<string>('');
  const [proveedorSearchLoading, setProveedorSearchLoading] = useState<boolean>(false);
  const [proveedoresEncontrados, setProveedoresEncontrados] = useState<ProveedorBusquedaItem[]>([]);
  const [codigoArticuloFiltro, setCodigoArticuloFiltro] = useState<string>('');
  const [descripcionArticuloFiltro, setDescripcionArticuloFiltro] = useState<string>('');
  const [dialogProductoOpen, setDialogProductoOpen] = useState<boolean>(false);
  const [productoSearchTerm, setProductoSearchTerm] = useState<string>('');
  const [productoSearchLoading, setProductoSearchLoading] = useState<boolean>(false);
  const [productosEncontrados, setProductosEncontrados] = useState<ProductoBusquedaItem[]>([]);
  const [busquedaDebounced, setBusquedaDebounced] = useState<string>('');
  const [busquedaResetKey, setBusquedaResetKey] = useState<number>(0);
  const [pageResultados, setPageResultados] = useState<number>(0);
  const [rowsPerPageResultados, setRowsPerPageResultados] = useState<number>(RESULTADOS_PAGE_SIZE_DEFAULT);
  const [fechaDesde, setFechaDesde] = useState<string>(defaultDateRange.from);
  const [fechaHasta, setFechaHasta] = useState<string>(defaultDateRange.to);
  const [filtroFechaRapida, setFiltroFechaRapida] = useState<FiltroFechaRapida>('ult3Meses');
  const [filtroPendiente, setFiltroPendiente] = useState<'todos' | 'pendientes' | 'facturar' | 'recibir' | 'ambos'>('pendientes');
  const [incluirGastos, setIncluirGastos] = useState<boolean>(false);
  const [soloInventariados, setSoloInventariados] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<CompraPendienteMaster[]>([]);
  const [hilo, setHilo] = useState<CompraHiloComprobante[]>([]);
  const [selectedRowKey, setSelectedRowKey] = useState<string>('');
  const [pendienteHiloInfo, setPendienteHiloInfo] = useState<{
    nroFormularioMaster: number;
    nroItemMaster: number;
    resumenLinea?: string;
  } | null>(null);
  const [usarTarjetas, setUsarTarjetas] = useState<boolean>(false);
  const listadoContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const updateLayoutMode = () => {
      const width = listadoContainerRef.current?.clientWidth ?? window.innerWidth;
      setUsarTarjetas(width < TABLE_MIN_WIDTH);
    };

    updateLayoutMode();

    const observer = new ResizeObserver(() => updateLayoutMode());
    if (listadoContainerRef.current) {
      observer.observe(listadoContainerRef.current);
    }

    window.addEventListener('resize', updateLayoutMode);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateLayoutMode);
    };
  }, []);

  const buscar = async (soloPendientesApi?: boolean) => {
    const nroProveedorNumber =
      nroProveedor.trim() === '' ? undefined : Number(nroProveedor.trim());

    if (nroProveedor.trim() !== '' && Number.isNaN(nroProveedorNumber)) {
      enqueueSnackbar('Ingresá un número de proveedor válido', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const data = await comprasSeguimientoService.getPendientesMaster(
        nroProveedorNumber,
        soloPendientesApi ?? filtroPendiente !== 'todos',
        incluirGastos
      );
      setRows(data);
      setHilo([]);
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudo cargar seguimiento de compras'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltroPendiente = (filtro: typeof filtroPendiente) => {
    const necesitaRefetch = (filtro === 'todos') !== (filtroPendiente === 'todos');
    setFiltroPendiente(filtro);
    if (necesitaRefetch) {
      void buscar(filtro !== 'todos');
    }
  };

  const buscarProveedores = async (searchTermOverride?: string) => {
    const term = (searchTermOverride ?? proveedorSearchTerm).trim();
    try {
      setProveedorSearchLoading(true);
      setProveedorSearchTerm(term);
      const data = await proveedorService.getProveedores({
        searchTerm: term || undefined,
        pageNumber: 1,
        pageSize: 30,
      });
      setProveedoresEncontrados(
        (data.items || []).map((p) => ({
          nroProveedor: p.nroProveedor,
          nombre: p.nombre,
        }))
      );
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudieron buscar proveedores'), { variant: 'error' });
    } finally {
      setProveedorSearchLoading(false);
    }
  };

  const seleccionarProveedor = (proveedor: ProveedorBusquedaItem) => {
    setNroProveedor(String(proveedor.nroProveedor));
    setNombreProveedorFiltro(proveedor.nombre ?? '');
    setProveedorSearchTerm(proveedor.nombre?.trim() || String(proveedor.nroProveedor));
    setDialogProveedorOpen(false);
  };

  const abrirBuscadorProveedores = () => {
    const term = nombreProveedorFiltro.trim() || nroProveedor.trim();
    setDialogProveedorOpen(true);
    void buscarProveedores(term);
  };

  const buscarProductos = async (searchTermOverride?: string) => {
    const term = (searchTermOverride ?? productoSearchTerm).trim();
    try {
      setProductoSearchLoading(true);
      setProductoSearchTerm(term);
      const data = await productoService.getProductos({
        searchTerm: term || undefined,
        pageNumber: 1,
        pageSize: 30,
      });
      setProductosEncontrados(
        (data.items || []).map((p) => ({
          codigoArticulo: p.codigoArticulo,
          descripcion: p.descripcion,
        }))
      );
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudieron buscar productos'), { variant: 'error' });
    } finally {
      setProductoSearchLoading(false);
    }
  };

  const seleccionarProducto = (producto: ProductoBusquedaItem) => {
    setCodigoArticuloFiltro(producto.codigoArticulo ?? '');
    setDescripcionArticuloFiltro(producto.descripcion ?? '');
    setProductoSearchTerm(producto.descripcion?.trim() || producto.codigoArticulo);
    setDialogProductoOpen(false);
  };

  const abrirBuscadorProductos = () => {
    const term = descripcionArticuloFiltro.trim() || codigoArticuloFiltro.trim();
    setDialogProductoOpen(true);
    void buscarProductos(term);
  };

  const aplicarFiltroFechaRapida = (filtro: Exclude<FiltroFechaRapida, 'custom'>) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let from = '';
    let to = '';

    switch (filtro) {
      case 'hoy':
        from = toInputDate(today);
        to = toInputDate(today);
        break;
      case 'ayer': {
        const ayer = new Date(today);
        ayer.setDate(ayer.getDate() - 1);
        from = toInputDate(ayer);
        to = toInputDate(ayer);
        break;
      }
      case 'estaSemana': {
        const day = today.getDay();
        const diffToMonday = day === 0 ? 6 : day - 1;
        const monday = new Date(today);
        monday.setDate(today.getDate() - diffToMonday);
        from = toInputDate(monday);
        to = toInputDate(today);
        break;
      }
      case 'ult3Meses': {
        const start = new Date(today);
        start.setMonth(start.getMonth() - 3);
        from = toInputDate(start);
        to = toInputDate(today);
        break;
      }
      case 'ult6Meses': {
        const start = new Date(today);
        start.setMonth(start.getMonth() - 6);
        from = toInputDate(start);
        to = toInputDate(today);
        break;
      }
      case 'todo':
      default:
        from = '';
        to = '';
        break;
    }

    setFechaDesde(from);
    setFechaHasta(to);
    setFiltroFechaRapida(filtro);
  };

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verHilo = async (r: CompraPendienteMaster) => {
    setSelectedRowKey(`${r.nroFormularioMaster}-${r.nroItemMaster}`);
    try {
      const data = await comprasSeguimientoService.getHilo(r.nroFormularioMaster, r.nroItemMaster);
      setHilo(data.comprobantes || []);
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudo cargar el hilo del comprobante'), { variant: 'error' });
    }
  };

  const irANuevaFactura = (r: CompraPendienteMaster) => {
    navigate('/compras/facturas/nuevo', { state: { compraPendiente: r } });
  };

  const irANuevoRemito = (r: CompraPendienteMaster) => {
    navigate('/compras/remitos/nuevo', { state: { compraPendiente: r } });
  };

  const abrirHiloInfo = (r: CompraPendienteMaster) => {
    setPendienteHiloInfo({
      nroFormularioMaster: r.nroFormularioMaster,
      nroItemMaster: r.nroItemMaster,
      resumenLinea: `${r.codigoArticulo ?? ''} - ${r.descripcionArticulo ?? ''}`.trim(),
    });
  };

  const buildSearchText = useCallback(
    (r: CompraPendienteMaster) =>
      [
        `${r.nroFormularioMaster}/${r.nroItemMaster}`,
        formatComprobanteMasterDisplay(r),
        r.nroProveedor ? String(r.nroProveedor) : '',
        r.nombreProveedor ?? '',
        r.codigoArticulo ?? '',
        r.descripcionArticulo ?? '',
      ].join(' '),
    []
  );

  const matchPendiente = useCallback((r: CompraPendienteMaster, filtro: string) => {
    if (filtro === 'pendientes') return r.pendienteFacturar > 0 || r.pendienteRecibir > 0;
    if (filtro === 'facturar') return r.pendienteFacturar > 0;
    if (filtro === 'recibir') return r.pendienteRecibir > 0;
    if (filtro === 'ambos') return r.pendienteFacturar > 0 && r.pendienteRecibir > 0;
    return true;
  }, []);

  const getNroProveedor = useCallback((r: CompraPendienteMaster) => r.nroProveedor, []);

  const rowsFiltradas = useFilasSeguimientoFiltradas({
    rows,
    busquedaDebounced,
    buildSearchText,
    filtroPendiente,
    matchPendiente,
    fechaDesde,
    fechaHasta,
    filtroEntidadNumero: nroProveedor,
    getEntidadNumero: getNroProveedor,
    codigoArticuloFiltro,
  });

  const rowsFiltradasVisibles = useMemo(() => {
    if (!soloInventariados) return rowsFiltradas;
    return rowsFiltradas.filter((r) => esProductoInventariado(r.invetariado));
  }, [rowsFiltradas, soloInventariados]);

  useEffect(() => {
    setPageResultados(0);
  }, [
    rowsFiltradasVisibles.length,
    busquedaDebounced,
    filtroPendiente,
    fechaDesde,
    fechaHasta,
    nroProveedor,
    codigoArticuloFiltro,
    soloInventariados,
  ]);

  const rowsPaginadas = useMemo(() => {
    const start = pageResultados * rowsPerPageResultados;
    return rowsFiltradasVisibles.slice(start, start + rowsPerPageResultados);
  }, [rowsFiltradasVisibles, pageResultados, rowsPerPageResultados]);

  const totalPendienteFacturar = useMemo(
    () => rowsFiltradasVisibles.reduce((acc, r) => acc + Number(r.pendienteFacturar || 0), 0),
    [rowsFiltradasVisibles]
  );
  const totalPendienteRecibir = useMemo(
    () => rowsFiltradasVisibles.reduce((acc, r) => acc + Number(r.pendienteRecibir || 0), 0),
    [rowsFiltradasVisibles]
  );

  const resumenFiltrosAvanzados = useMemo(() => {
    const partes: string[] = [];
    if (nroProveedor.trim() || nombreProveedorFiltro.trim()) partes.push('Proveedor');
    if (codigoArticuloFiltro.trim() || descripcionArticuloFiltro.trim()) partes.push('Producto');
    if (filtroFechaRapida === 'custom') partes.push('Fechas personalizadas');
    if (incluirGastos) partes.push('Incluye gastos');
    if (!soloInventariados) partes.push('Incluye no inventariados');
    if (filtroPendiente !== 'pendientes') {
      const etiquetas = {
        facturar: 'Pend. facturar',
        recibir: 'Pend. recibir',
        ambos: 'Ambos pendientes',
        todos: 'Ver todo',
      } as const;
      partes.push(etiquetas[filtroPendiente]);
    }
    return partes.length > 0 ? partes.join(' · ') : '';
  }, [
    nroProveedor,
    nombreProveedorFiltro,
    codigoArticuloFiltro,
    descripcionArticuloFiltro,
    filtroFechaRapida,
    incluirGastos,
    soloInventariados,
    filtroPendiente,
  ]);

  const limpiarFiltros = () => {
    setNroProveedor('');
    setNombreProveedorFiltro('');
    setCodigoArticuloFiltro('');
    setDescripcionArticuloFiltro('');
    setBusquedaDebounced('');
    setBusquedaResetKey((k) => k + 1);
    setPageResultados(0);
    setFechaDesde(defaultDateRange.from);
    setFechaHasta(defaultDateRange.to);
    setFiltroFechaRapida('ult3Meses');
    setFiltroPendiente('pendientes');
    setIncluirGastos(false);
    setSoloInventariados(true);
    setHilo([]);
    setRows([]);
  };

  const onFilterKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      buscar();
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/compras/informes')}
                sx={{ textTransform: 'none' }}
              >
                Informes
              </Button>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Seguimiento de compras
                </Typography>
              </Box>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'flex-start' } }}
            >
              <Button variant="contained" color="primary" onClick={() => { void buscar(); }} disabled={loading}>
                {loading ? <CircularProgress size={18} color="inherit" /> : 'Buscar'}
              </Button>
              <Button variant="text" onClick={limpiarFiltros}>Limpiar</Button>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600 }}>
              Período rápido
            </Typography>
            <Chip
              label="Hoy"
              size="small"
              color={filtroFechaRapida === 'hoy' ? 'primary' : 'default'}
              variant={filtroFechaRapida === 'hoy' ? 'filled' : 'outlined'}
              onClick={() => aplicarFiltroFechaRapida('hoy')}
            />
            <Chip
              label="Ayer"
              size="small"
              color={filtroFechaRapida === 'ayer' ? 'primary' : 'default'}
              variant={filtroFechaRapida === 'ayer' ? 'filled' : 'outlined'}
              onClick={() => aplicarFiltroFechaRapida('ayer')}
            />
            <Chip
              label="Esta semana"
              size="small"
              color={filtroFechaRapida === 'estaSemana' ? 'primary' : 'default'}
              variant={filtroFechaRapida === 'estaSemana' ? 'filled' : 'outlined'}
              onClick={() => aplicarFiltroFechaRapida('estaSemana')}
            />
            <Chip
              label="Ult. 3 meses"
              size="small"
              color={filtroFechaRapida === 'ult3Meses' ? 'primary' : 'default'}
              variant={filtroFechaRapida === 'ult3Meses' ? 'filled' : 'outlined'}
              onClick={() => aplicarFiltroFechaRapida('ult3Meses')}
            />
            <Chip
              label="Ult. 6 meses"
              size="small"
              color={filtroFechaRapida === 'ult6Meses' ? 'primary' : 'default'}
              variant={filtroFechaRapida === 'ult6Meses' ? 'filled' : 'outlined'}
              onClick={() => aplicarFiltroFechaRapida('ult6Meses')}
            />
            <Chip
              label="Todo"
              size="small"
              color={filtroFechaRapida === 'todo' ? 'primary' : 'default'}
              variant={filtroFechaRapida === 'todo' ? 'filled' : 'outlined'}
              onClick={() => aplicarFiltroFechaRapida('todo')}
            />
          </Stack>

          <Accordion
            variant="outlined"
            disableGutters
            sx={{
              mb: 2,
              borderRadius: 2,
              '&:before': { display: 'none' },
              boxShadow: 'none',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: '#fafafa', minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, pr: 1 }}>
                <FilterAltIcon color="primary" fontSize="small" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Filtros avanzados
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {resumenFiltrosAvanzados}
                  </Typography>
                </Box>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
            <TextField
              label="Fecha desde"
              type="date"
              value={fechaDesde}
              onChange={(e) => {
                setFechaDesde(e.target.value);
                setFiltroFechaRapida('custom');
              }}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Fecha hasta"
              type="date"
              value={fechaHasta}
              onChange={(e) => {
                setFechaHasta(e.target.value);
                setFiltroFechaRapida('custom');
              }}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <TextField
              label="Nro proveedor"
              value={nroProveedor}
              onChange={(e) => {
                setNroProveedor(e.target.value);
                setNombreProveedorFiltro('');
              }}
              onKeyDown={onFilterKeyDown}
              placeholder="Ej: 1001"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      size="small"
                      color="primary"
                      title="Buscar proveedor"
                      onClick={abrirBuscadorProveedores}
                    >
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Nombre proveedor"
              value={nombreProveedorFiltro}
              placeholder="Sin selección"
              size="small"
              onChange={(e) => {
                setNombreProveedorFiltro(e.target.value);
                setProveedorSearchTerm(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setDialogProveedorOpen(true);
                  void buscarProveedores((e.target as HTMLInputElement).value);
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      size="small"
                      color="primary"
                      title="Buscar proveedor por nombre"
                      onClick={abrirBuscadorProveedores}
                    >
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <TextField
              label="Código de producto"
              value={codigoArticuloFiltro}
              placeholder="Código de artículo"
              size="small"
              onChange={(e) => {
                setCodigoArticuloFiltro(e.target.value);
                setDescripcionArticuloFiltro('');
              }}
              onKeyDown={onFilterKeyDown}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      size="small"
                      color="primary"
                      title="Buscar producto"
                      onClick={abrirBuscadorProductos}
                    >
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Descripción producto"
              value={descripcionArticuloFiltro}
              placeholder="Sin selección"
              size="small"
              onChange={(e) => {
                setDescripcionArticuloFiltro(e.target.value);
                setProductoSearchTerm(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setDialogProductoOpen(true);
                  void buscarProductos((e.target as HTMLInputElement).value);
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      size="small"
                      color="primary"
                      title="Buscar producto por descripción"
                      onClick={abrirBuscadorProductos}
                    >
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }} alignItems="center" useFlexGap>
            <FormControlLabel
              control={(
                <Switch
                  checked={incluirGastos}
                  onChange={(e) => setIncluirGastos(e.target.checked)}
                />
              )}
              label="Incluir gastos (Tipo proveedor = G)"
              sx={{ ml: 0, mr: 0 }}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={soloInventariados}
                  onChange={(e) => setSoloInventariados(e.target.checked)}
                />
              )}
              label="Solo inventariados"
              sx={{ ml: 0, mr: 0 }}
            />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} alignItems="center" useFlexGap>
            <Chip
              label="Pendientes"
              color={filtroPendiente === 'pendientes' ? 'primary' : 'default'}
              variant={filtroPendiente === 'pendientes' ? 'filled' : 'outlined'}
              onClick={() => aplicarFiltroPendiente('pendientes')}
            />
            <Chip
              label="Con ambos pendientes"
              color={filtroPendiente === 'ambos' ? 'primary' : 'default'}
              variant={filtroPendiente === 'ambos' ? 'filled' : 'outlined'}
              onClick={() => aplicarFiltroPendiente('ambos')}
            />
            <Chip
              label="Con pendiente de facturar"
              color={filtroPendiente === 'facturar' ? 'primary' : 'default'}
              variant={filtroPendiente === 'facturar' ? 'filled' : 'outlined'}
              onClick={() => aplicarFiltroPendiente('facturar')}
            />
            <Chip
              label="Con pendiente de recibir"
              color={filtroPendiente === 'recibir' ? 'primary' : 'default'}
              variant={filtroPendiente === 'recibir' ? 'filled' : 'outlined'}
              onClick={() => aplicarFiltroPendiente('recibir')}
            />
            <Chip
              label="Ver todo"
              color={filtroPendiente === 'todos' ? 'primary' : 'default'}
              variant={filtroPendiente === 'todos' ? 'filled' : 'outlined'}
              onClick={() => aplicarFiltroPendiente('todos')}
            />
          </Stack>
            </AccordionDetails>
          </Accordion>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <KpiTarjeta
                icon={<FormatListNumberedIcon fontSize="small" />}
                titulo="Resultados"
                valor={rowsFiltradasVisibles.length.toLocaleString('es-AR')}
                colorIcono="#1565c0"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KpiTarjeta
                icon={<ReceiptLongOutlinedIcon fontSize="small" />}
                titulo="Pendiente total de facturar"
                valor={totalPendienteFacturar.toLocaleString('es-AR')}
                colorIcono="#8e24aa"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KpiTarjeta
                icon={<LocalShippingOutlinedIcon fontSize="small" />}
                titulo="Pendiente total de recibir"
                valor={totalPendienteRecibir.toLocaleString('es-AR')}
                colorIcono="#43a047"
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          <Box ref={listadoContainerRef} sx={{ mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <BusquedaEnResultadosField
                key={busquedaResetKey}
                onDebouncedChange={setBusquedaDebounced}
                placeholder="Proveedor, artículo, comprobante o master"
              />
            </Box>
            {!usarTarjetas && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Comprobante</TableCell>
                      <TableCell>Fecha emisión</TableCell>
                      <TableCell>Proveedor</TableCell>
                      <TableCell>Artículo</TableCell>
                      <TableCell align="right">Pedida</TableCell>
                      <TableCell align="right">Devuelta</TableCell>
                      <TableCell align="right">Pend. facturar</TableCell>
                      <TableCell align="right">Pend. recibir</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!loading && rowsPaginadas.map((r) => {
                      const rowKey = `${r.nroFormularioMaster}-${r.nroItemMaster}`;
                      return (
                        <TableRow
                          key={rowKey}
                          hover
                          selected={selectedRowKey === rowKey}
                          onClick={() => verHilo(r)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatComprobanteMasterDisplay(r)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatFechaEmisionComprobanteMaster(r.fechaEmisionComprobanteMaster)}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Tooltip title={`${r.nroProveedor ?? '-'} - ${r.nombreProveedor ?? '-'}`}>
                              <Typography variant="body2" noWrap>
                                {r.nroProveedor ?? '-'} - {r.nombreProveedor ?? '-'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Tooltip title={`${r.codigoArticulo ?? '-'} - ${r.descripcionArticulo ?? '-'}`}>
                              <Typography variant="body2" noWrap>
                                {r.codigoArticulo ?? '-'} - {r.descripcionArticulo ?? '-'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{r.cantidadPedida}</TableCell>
                          <TableCell align="right">{r.cantidadDevuelta}</TableCell>
                          <TableCell align="right">{r.pendienteFacturar}</TableCell>
                          <TableCell align="right">{r.pendienteRecibir}</TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="Facturado y remitido (detalle)">
                                <IconButton
                                  size="small"
                                  sx={ACTION_ICON_BUTTON_SX.verDetalle}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    abrirHiloInfo(r);
                                  }}
                                  aria-label="Información facturado y remitido"
                                >
                                  <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Can permiso="compras.facturas.nuevo">
                              <Tooltip title="Nueva factura">
                                <IconButton
                                  size="small"
                                  sx={ACTION_ICON_BUTTON_SX.nuevaFactura}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    irANuevaFactura(r);
                                  }}
                                  aria-label="Nueva factura"
                                >
                                  <ReceiptLongOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              </Can>
                              <Can permiso="compras.remitos.nuevo">
                              <Tooltip title="Nuevo remito">
                                <IconButton
                                  size="small"
                                  sx={ACTION_ICON_BUTTON_SX.nuevoRemito}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    irANuevoRemito(r);
                                  }}
                                  aria-label="Nuevo remito"
                                >
                                  <LocalShippingOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              </Can>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {loading && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading && rowsFiltradasVisibles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No hay resultados para los filtros seleccionados.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {usarTarjetas && (
              <Stack spacing={1.2}>
                {loading && (
                  <Card variant="outlined" sx={{ py: 4 }}>
                    <Stack alignItems="center">
                      <CircularProgress size={24} />
                    </Stack>
                  </Card>
                )}

                {!loading && rowsFiltradasVisibles.length === 0 && (
                  <Card variant="outlined" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No hay resultados para los filtros seleccionados.
                    </Typography>
                  </Card>
                )}

                {!loading && rowsPaginadas.map((r) => {
                  const rowKey = `${r.nroFormularioMaster}-${r.nroItemMaster}`;
                  const selected = selectedRowKey === rowKey;
                  return (
                    <Card
                      key={rowKey}
                      variant="outlined"
                      onClick={() => verHilo(r)}
                      sx={{
                        cursor: 'pointer',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'action.selected' : 'background.paper',
                      }}
                    >
                      <Stack spacing={1.2} sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>
                          {formatComprobanteMasterDisplay(r)}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          Fecha emisión: {formatFechaEmisionComprobanteMaster(r.fechaEmisionComprobanteMaster)}
                        </Typography>

                        <Tooltip title={`${r.nroProveedor ?? '-'} - ${r.nombreProveedor ?? '-'}`}>
                          <Typography variant="body2" noWrap>
                            Proveedor: {r.nroProveedor ?? '-'} - {r.nombreProveedor ?? '-'}
                          </Typography>
                        </Tooltip>

                        <Tooltip title={`${r.codigoArticulo ?? '-'} - ${r.descripcionArticulo ?? '-'}`}>
                          <Typography variant="body2" noWrap>
                            Articulo: {r.codigoArticulo ?? '-'} - {r.descripcionArticulo ?? '-'}
                          </Typography>
                        </Tooltip>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <Typography variant="body2">Pedida: {r.cantidadPedida}</Typography>
                          <Typography variant="body2">Devuelta: {r.cantidadDevuelta}</Typography>
                          <Typography variant="body2">Pend. facturar: {r.pendienteFacturar}</Typography>
                          <Typography variant="body2">Pend. recibir: {r.pendienteRecibir}</Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Tooltip title="Facturado y remitido (detalle)">
                            <IconButton
                              size="small"
                              sx={ACTION_ICON_BUTTON_SX.verDetalle}
                              onClick={(event) => {
                                event.stopPropagation();
                                abrirHiloInfo(r);
                              }}
                              aria-label="Información facturado y remitido"
                            >
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Can permiso="compras.facturas.nuevo">
                          <Tooltip title="Nueva factura">
                            <IconButton
                              size="small"
                              sx={ACTION_ICON_BUTTON_SX.nuevaFactura}
                              onClick={(event) => {
                                event.stopPropagation();
                                irANuevaFactura(r);
                              }}
                              aria-label="Nueva factura"
                            >
                              <ReceiptLongOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          </Can>
                          <Can permiso="compras.remitos.nuevo">
                          <Tooltip title="Nuevo remito">
                            <IconButton
                              size="small"
                              sx={ACTION_ICON_BUTTON_SX.nuevoRemito}
                              onClick={(event) => {
                                event.stopPropagation();
                                irANuevoRemito(r);
                              }}
                              aria-label="Nuevo remito"
                            >
                              <LocalShippingOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          </Can>
                        </Stack>
                      </Stack>
                    </Card>
                  );
                })}
              </Stack>
            )}

            {!loading && rowsFiltradasVisibles.length > 0 && (
              <TablePagination
                component="div"
                count={rowsFiltradasVisibles.length}
                page={pageResultados}
                onPageChange={(_event, newPage) => setPageResultados(newPage)}
                rowsPerPage={rowsPerPageResultados}
                onRowsPerPageChange={(event) => {
                  setRowsPerPageResultados(parseInt(event.target.value, 10));
                  setPageResultados(0);
                }}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage="Filas por página"
              />
            )}
          </Box>

          <Dialog open={dialogProveedorOpen} onClose={() => setDialogProveedorOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Buscar proveedor</DialogTitle>
            <DialogContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, mt: 0.5 }}>
                <TextField
                  label="Nombre o nro proveedor"
                  value={proveedorSearchTerm}
                  onChange={(e) => setProveedorSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      buscarProveedores();
                    }
                  }}
                  size="small"
                  fullWidth
                />
                <Button variant="contained" onClick={() => { void buscarProveedores(); }} disabled={proveedorSearchLoading}>
                  Buscar
                </Button>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nro proveedor</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell align="center">Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {proveedorSearchLoading && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={22} />
                        </TableCell>
                      </TableRow>
                    )}
                    {!proveedorSearchLoading && proveedoresEncontrados.map((proveedor) => (
                      <TableRow key={proveedor.nroProveedor} hover>
                        <TableCell>{proveedor.nroProveedor}</TableCell>
                        <TableCell>{proveedor.nombre ?? '-'}</TableCell>
                        <TableCell align="center">
                          <Button size="small" variant="outlined" onClick={() => seleccionarProveedor(proveedor)}>
                            Seleccionar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!proveedorSearchLoading && proveedoresEncontrados.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            Sin resultados.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogProductoOpen} onClose={() => setDialogProductoOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Buscar producto</DialogTitle>
            <DialogContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, mt: 0.5 }}>
                <TextField
                  label="Código o descripción"
                  value={productoSearchTerm}
                  onChange={(e) => setProductoSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      buscarProductos();
                    }
                  }}
                  size="small"
                  fullWidth
                />
                <Button variant="contained" onClick={() => { void buscarProductos(); }} disabled={productoSearchLoading}>
                  Buscar
                </Button>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell align="center">Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productoSearchLoading && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={22} />
                        </TableCell>
                      </TableRow>
                    )}
                    {!productoSearchLoading && productosEncontrados.map((producto) => (
                      <TableRow key={producto.codigoArticulo} hover>
                        <TableCell>{producto.codigoArticulo}</TableCell>
                        <TableCell>{producto.descripcion ?? '-'}</TableCell>
                        <TableCell align="center">
                          <Button size="small" variant="outlined" onClick={() => seleccionarProducto(producto)}>
                            Seleccionar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!productoSearchLoading && productosEncontrados.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            Sin resultados.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
          </Dialog>

          <Typography variant="h6" sx={{ mb: 1 }}>Hilo documental</Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Formulario</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Comprobante</TableCell>
                  <TableCell>Artículo</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hilo.map((h) => (
                  <TableRow key={`${h.nroFormulario}-${h.nroItem}-${h.nroTipoFormulario}`}>
                    <TableCell>{h.nroFormulario}/{h.nroItem}</TableCell>
                    <TableCell>{h.fechaEmision ? new Date(h.fechaEmision).toLocaleDateString('es-AR') : ''}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatComprobanteHiloLine(h)}</TableCell>
                    <TableCell>{h.codigoArticulo} - {h.descripcionArticulo}</TableCell>
                    <TableCell align="right">{h.cantidad}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <CompraPendienteHiloInfoDialog
        open={!!pendienteHiloInfo}
        onClose={() => setPendienteHiloInfo(null)}
        nroFormularioMaster={pendienteHiloInfo?.nroFormularioMaster ?? null}
        nroItemMaster={pendienteHiloInfo?.nroItemMaster ?? null}
        resumenLinea={pendienteHiloInfo?.resumenLinea}
      />
    </Box>
  );
};

export default SeguimientoCompras;
